import * as React from 'react';

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useModals } from '@shared/contexts';

import { Button } from '@components/button';
import { Modal } from '@components/modal';
import { ModalName } from '@components/modals/types.ts';
import { Tabs } from '@components/tabs';

import { config } from '@/config.ts';

import * as Styled from './auth-modal.styled';
import { Form } from './components/form';
import { Help } from './components/help';
import { AUTH_TAB, AuthTabsType } from './types.ts';

const isNgu3 = config.BRAND === 'ngu3';
const isRockets = config.BRAND === 'rockets';
const isBlitz = config.BRAND === 'blitz';
const isSk2 = config.BRAND === 'sk2';
const isCom2 = config.BRAND === 'com2';

const AuthModal = () => {
	const { t } = useTranslation(['errors', 'reg']);
	const { isModalOpen, closeModal, openModal } = useModals();

	const isExternalBrand = isNgu3 || isRockets || isBlitz || isSk2 || isCom2;

	const [activeTab, setActiveTab] = React.useState<AUTH_TAB>(isExternalBrand ? AUTH_TAB.BANK_ACCOUNT : AUTH_TAB.PHONE);

	const tabList: AuthTabsType[] = React.useMemo(() => {
		const baseTabs: AuthTabsType[] = [
			{ id: AUTH_TAB.PHONE, name: t('phone') },
			{ id: AUTH_TAB.BANK_ACCOUNT, name: t('account') },
			{ id: AUTH_TAB.EMAIL, name: t('email') }
		];

		if (config.ENV === 'development') {
			baseTabs.push({ id: AUTH_TAB.LOCAL, name: 'Локально' });
		}

		return baseTabs;
	}, [t]);

	const setTabId = (id: string) => {
		setActiveTab(id as AUTH_TAB);
	};

	const handleCloseModal = () => {
		setActiveTab(isExternalBrand ? AUTH_TAB.BANK_ACCOUNT : AUTH_TAB.PHONE);
		closeModal();
	};

	return (
		<Modal
			isOpen={isModalOpen(ModalName.LOGIN)}
			closeModal={handleCloseModal}
			maxWidth={500}
			size='large'
			hasPadding={false}
			backgroundColor='dark'
			titlePosition='center'
			title={t(activeTab === AUTH_TAB.HELP ? 'enter_help' : 'enter')}
		>
			<Styled.Wrapper>
				{activeTab === AUTH_TAB.HELP ? (
					<Help goBack={() => setActiveTab(isExternalBrand ? AUTH_TAB.BANK_ACCOUNT : AUTH_TAB.PHONE)} />
				) : (
					<Styled.Body>
						<Tabs tabList={tabList} activeTab={activeTab} setActiveTab={setTabId} isGray isWide isRounded />
						<Form shouldReload activeTab={activeTab} closeModal={closeModal} />
						{!isExternalBrand && (
							<Styled.RegisterActionWrapper>
								{t('reg:no_account')}{' '}
								{isRockets ? (
									<Link to={'https://parik-24.io/regmail/'} target='_blank'>
										<Styled.Action>{t('to_register')}</Styled.Action>
									</Link>
								) : (
									<Styled.Action onClick={() => openModal(ModalName.REGISTER)}>{t('to_register')}</Styled.Action>
								)}
							</Styled.RegisterActionWrapper>
						)}
						{!isExternalBrand && (
							<Styled.Footer>
								<Button color='ghost' onClick={() => setActiveTab(AUTH_TAB.HELP)}>
									{t('enter_help')}
								</Button>
								<Styled.RulesWrapper>
									{t('reg:by_accepting_reg')}
									<Button onClick={() => openModal(ModalName.OFFER_RULES)} color='ghost'>
										{t('reg:rules_terms')}
									</Button>
								</Styled.RulesWrapper>
							</Styled.Footer>
						)}
					</Styled.Body>
				)}
			</Styled.Wrapper>
		</Modal>
	);
};

export { AuthModal };
